import { useEffect, useRef, useState } from "react"
import alexAvatarSrc from "./assets/alex-avatar.png"
import clientAvatarSrc from "./assets/client-avatar.png"
import consultantThomasSrc from "./assets/consultant-thomas.png"
import consultantRobertSrc from "./assets/consultant-robert.png"
import consultantSofiaSrc from "./assets/consultant-sofia.png"
import styles from "./startups-landing-sections.module.css"

const PHASE_GREETING = 0
const PHASE_TYPE = 1
const PHASE_USER_SHOWN = 2
const PHASE_ALEX_REPLY = 3
const PHASE_RESULTS = 4
const PHASE_CARD_CLICK = 5
const PHASE_PROFILE = 6
const PHASE_CONTACT_CLICK = 7

const ALEX_GREETING = "Hello! Tell me about the role you're looking to fill."

const CLIENT_MESSAGE =
	"Hey Alex, I'm looking for a fractional CTO for our new project. It's an agentic voice app in the mental health space — LLM-based with real-time voice. Need someone hands-on, 2-3 days a week, ideally with experience in regulated or health-adjacent products."

const ALEX_REPLY = "On it. I'll have a scored shortlist ready for you shortly."

const CONSULTANTS = [
	{
		img: consultantThomasSrc,
		name: "Thomas V.",
		title: "Fractional CTO · Digital Health",
		rate: "€950/day",
		type: "Fractional",
	},
	{
		img: consultantRobertSrc,
		name: "Robert M.",
		title: "Technology Leader · Health-Tech",
		rate: "€1,200/day",
		type: "Advisory",
	},
	{
		img: consultantSofiaSrc,
		name: "Sofia L.",
		title: "VP Engineering · MedTech",
		rate: "€880/day",
		type: "Fractional",
	},
]

const PROFILE = {
	img: consultantRobertSrc,
	name: "Robert M.",
	title: "CTO-as-a-Service · Technology Leader",
	engagement: "Advisory",
	rate: "€1,200",
	availability: "2–3 days/week",
	location: "Amsterdam, NL",
	domains: ["Digital Health", "Regulated Products", "LLM Infrastructure"],
	bio: "20+ years building engineering teams in health-adjacent and regulated sectors. Former VP Engineering at a Series B telemedicine platform. Led two successful tech exits. Expert in voice AI and real-time systems.",
}

const GREETING_MS = 2200
const TYPE_CHAR_MS = 12
const USER_TO_REPLY_MS = 700
const SEARCH_MS = 3000
const RESULTS_HOLD_MS = 2000
const CARD_CLICK_MS = 700
const PROFILE_HOLD_MS = 4200
const CONTACT_CLICK_MS = 900
const RECYCLE_DELAY_MS = 1600

function AlexFace({ className }) {
	return <img src={alexAvatarSrc} alt="" className={className} draggable={false} />
}

function ClientFace() {
	return <img src={clientAvatarSrc} alt="" className={styles.msgUserFaceImg} draggable={false} />
}

export default function AlexScoutDemo() {
	const [phase, setPhase] = useState(PHASE_GREETING)
	const [reducedMotion, setReducedMotion] = useState(false)
	const timeoutsRef = useRef([])

	const clearAll = () => {
		timeoutsRef.current.forEach(clearTimeout)
		timeoutsRef.current = []
	}

	useEffect(() => {
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
		setReducedMotion(mq.matches)
		const onChange = () => setReducedMotion(mq.matches)
		mq.addEventListener("change", onChange)
		return () => mq.removeEventListener("change", onChange)
	}, [])

	useEffect(() => {
		if (reducedMotion) setPhase(PHASE_RESULTS)
	}, [reducedMotion])

	/* 0 → 1 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_GREETING) return undefined
		const t = setTimeout(() => setPhase(PHASE_TYPE), GREETING_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 1 → 2: simulate typing delay */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_TYPE) return undefined
		const t = setTimeout(() => setPhase(PHASE_USER_SHOWN), CLIENT_MESSAGE.length * TYPE_CHAR_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 2 → 3 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_USER_SHOWN) return undefined
		const t = setTimeout(() => setPhase(PHASE_ALEX_REPLY), USER_TO_REPLY_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 3 → 4 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_ALEX_REPLY) return undefined
		const t = setTimeout(() => setPhase(PHASE_RESULTS), SEARCH_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 4 → 5 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_RESULTS) return undefined
		const t = setTimeout(() => setPhase(PHASE_CARD_CLICK), RESULTS_HOLD_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 5 → 6 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_CARD_CLICK) return undefined
		const t = setTimeout(() => setPhase(PHASE_PROFILE), CARD_CLICK_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 6 → 7 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_PROFILE) return undefined
		const t = setTimeout(() => setPhase(PHASE_CONTACT_CLICK), PROFILE_HOLD_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 7 → recycle */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_CONTACT_CLICK) return undefined
		const t = setTimeout(() => {
			clearAll()
			setPhase(PHASE_GREETING)
		}, CONTACT_CLICK_MS + RECYCLE_DELAY_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	const showProfile = phase >= PHASE_PROFILE
	/* Hide early chat messages once results arrive to avoid scroll */
	const hideEarlyChat = phase >= PHASE_RESULTS

	return (
		<div
			className={`${styles.annaDemo} ${styles.alexDemo} ${showProfile ? styles.alexDemoProfileMode : ""}`}
			aria-hidden="true"
		>
			{/* ── Header — hidden in profile mode (profile fills the full popup) ── */}
			{!showProfile && (
				<div className={styles.annaDemoHeader}>
					<AlexFace className={styles.annaHeaderFace} />
					<div>
						<div className={styles.annaDemoName}>Alex</div>
						<div className={styles.annaDemoRole}>AI Scout</div>
					</div>
				</div>
			)}

			{/* ── Chat thread (phases 0–5) ── */}
			{!showProfile ? (
				<div className={styles.annaMessages}>
					{/* Greeting — hidden once results appear */}
					{!hideEarlyChat && (
						<div className={`${styles.msg} ${styles.msgAnna}`}>
							<AlexFace className={styles.msgAnnaFace} />
							<div className={styles.msgBubble}>{ALEX_GREETING}</div>
						</div>
					)}

					{/* Client message — hidden once results appear */}
					{!hideEarlyChat && phase >= PHASE_USER_SHOWN && (
						<div className={`${styles.msg} ${styles.msgUser}`}>
							<ClientFace />
							<div className={styles.msgBubble}>{CLIENT_MESSAGE}</div>
						</div>
					)}

					{/* Alex reply — hidden once results appear */}
					{!hideEarlyChat && phase >= PHASE_ALEX_REPLY && (
						<div className={`${styles.msg} ${styles.msgAnna}`}>
							<AlexFace className={styles.msgAnnaFace} />
							<div className={styles.msgBubble}>{ALEX_REPLY}</div>
						</div>
					)}

					{/* Searching block */}
					{phase === PHASE_ALEX_REPLY && (
						<div className={styles.annaSearchingBlock}>
							<AlexFace className={styles.annaSearchingFace} />
							<div className={styles.annaSearchingBody}>
								<div className={styles.annaSearchingTitle}>Searching Techcore Network</div>
								<div className={styles.typingDots} aria-hidden>
									<span />
									<span />
									<span />
								</div>
							</div>
						</div>
					)}

					{/* Consultant result cards */}
					{phase >= PHASE_RESULTS && (
						<div className={`${styles.msg} ${styles.msgAnna}`}>
							<AlexFace className={styles.msgAnnaFace} />
							<div className={styles.msgBubble}>
								Found <strong className={styles.strongGold}>3 consultants</strong> matching your brief:
								<div className={styles.alexCards}>
									{CONSULTANTS.map((c, i) => (
										<div
											key={c.name}
											className={`${styles.alexCard} ${phase === PHASE_CARD_CLICK && i === 1 ? styles.alexCardClicked : ""}`}
										>
											{c.img ? (
												<img src={c.img} alt="" className={styles.alexCardPhoto} draggable={false} />
											) : (
												<div className={styles.alexCardInitials}>{c.initials}</div>
											)}
											<div className={styles.alexCardInfo}>
												<div className={styles.alexCardName}>{c.name}</div>
												<div className={styles.alexCardTitle}>{c.title}</div>
											</div>
											<div className={styles.alexCardRight}>
												<div className={styles.alexCardRate}>{c.rate}</div>
												<div className={styles.alexCardBadge}>{c.type}</div>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					)}
				</div>
			) : (
				/* ── Profile panel — full popup, no header ── */
				<div className={styles.alexProfileWrap}>
					<div className={styles.alexProfHead}>
						<img src={PROFILE.img} alt="" className={styles.alexProfPhoto} draggable={false} />
						<div className={styles.alexProfHeadInfo}>
							<div className={styles.alexProfName}>{PROFILE.name}</div>
							<div className={styles.alexProfTitle}>{PROFILE.title}</div>
						</div>
					</div>

					<p className={styles.alexProfBio}>{PROFILE.bio}</p>

					<div className={styles.alexProfMeta}>
						<div className={styles.alexProfMetaItem}>
							<span className={styles.alexProfMetaKey}>Engagement</span>
							<span className={styles.alexProfMetaVal}>{PROFILE.engagement}</span>
						</div>
						<div className={styles.alexProfMetaItem}>
							<span className={styles.alexProfMetaKey}>Rate</span>
							<span className={styles.alexProfMetaVal}>{PROFILE.rate}/day</span>
						</div>
						<div className={styles.alexProfMetaItem}>
							<span className={styles.alexProfMetaKey}>Availability</span>
							<span className={styles.alexProfMetaVal}>{PROFILE.availability}</span>
						</div>
						<div className={styles.alexProfMetaItem}>
							<span className={styles.alexProfMetaKey}>Location</span>
							<span className={styles.alexProfMetaVal}>{PROFILE.location}</span>
						</div>
					</div>

					<div className={styles.alexProfDomains}>
						{PROFILE.domains.map((d) => (
							<span key={d} className={styles.alexProfDomain}>
								{d}
							</span>
						))}
					</div>

					<button
						className={`${styles.alexContactBtn} ${phase === PHASE_CONTACT_CLICK ? styles.alexContactBtnPressed : ""}`}
						tabIndex={-1}
					>
						Contact Robert →
					</button>
				</div>
			)}
		</div>
	)
}
